// src/store/sensorStore.js
import { defineStore } from 'pinia';
import { ref } from 'vue';
import { useFetch } from '@/plugins/fetch';

export const useSensorStore = defineStore('sensors', () => {
    const fetch = useFetch();

    // State
    const sensors = ref([]);
    const currentSensor = ref(null);
    const isLoadingList = ref(false);
    const isLoadingDetails = ref(false);

    // Error states
    const error = ref(null);
    const errorDetails = ref(null);

    // Clear errors
    function clearErrors() {
        error.value = null;
        errorDetails.value = null;
    }

    // Actions
    async function fetchSensors(force = false) {
        if (sensors.value.length > 0 && !force) {
            return;
        }
        isLoadingList.value = true;
        error.value = null;
        try {
            const response = await fetch('sensors/');
            // Handle API response format: { success, data, error, meta }
            const sensorData = response?.data || response;
            // Convert object keyed by ID to array
            sensors.value = Array.isArray(sensorData) ? sensorData : Object.values(sensorData || {});
        } catch (err) {
            error.value = 'Failed to fetch sensors';
            errorDetails.value = err?.message || 'Unknown error';
        } finally {
            isLoadingList.value = false;
        }
    }

    async function fetchSensorById(uniqueId) {
        isLoadingDetails.value = true;
        error.value = null;
        try {
            const response = await fetch(`sensors/${uniqueId}`);
            // Handle API response format
            currentSensor.value = response?.data || response;
        } catch (err) {
            error.value = `Failed to fetch sensor ${uniqueId}`;
            errorDetails.value = err?.message || 'Unknown error';
            currentSensor.value = null;
        } finally {
            isLoadingDetails.value = false;
        }
    }

    async function createSensor(sensorData) {
        error.value = null;
        try {
            const response = await fetch('sensors/', {
                method: 'POST',
                body: sensorData
            });
            sensors.value = [];
            await fetchSensors();
            return response;
        } catch (err) {
            error.value = 'Failed to create sensor';
            errorDetails.value = err?.message || 'Unknown error';
            throw err;
        }
    }

    async function updateSensor(uniqueId, sensorData) {
        error.value = null;
        try {
            const response = await fetch(`sensors/${uniqueId}/`, {
                method: 'PUT',
                body: sensorData
            });
            // Update in local state
            const index = sensors.value.findIndex(s => s.unique_id === uniqueId);
            if (index !== -1) {
                sensors.value[index] = { ...sensors.value[index], ...response };
            }
            if (currentSensor.value?.unique_id === uniqueId) {
                currentSensor.value = { ...currentSensor.value, ...response };
            }
            return response;
        } catch (err) {
            error.value = 'Failed to update sensor';
            errorDetails.value = err?.message || 'Unknown error';
            throw err;
        }
    }

    async function deleteSensor(uniqueId) {
        error.value = null;
        try {
            await fetch(`sensors/${uniqueId}/`, {
                method: 'DELETE'
            });
            sensors.value = sensors.value.filter(s => s.unique_id !== uniqueId);
            if (currentSensor.value?.unique_id === uniqueId) {
                currentSensor.value = null;
            }
        } catch (err) {
            error.value = 'Failed to delete sensor';
            errorDetails.value = err?.message || 'Unknown error';
            throw err;
        }
    }

    // A getter to find a sensor from the already loaded list
    const getSensorFromList = (uniqueId) => {
        return sensors.value.find(s => s.unique_id === uniqueId);
    }

    return {
        sensors,
        currentSensor,
        isLoadingList,
        isLoadingDetails,
        error,
        errorDetails,
        clearErrors,
        fetchSensors,
        fetchSensorById,
        createSensor,
        updateSensor,
        deleteSensor,
        getSensorFromList
    };
});